import { SystemPlan } from "@ews/react-data";
import { useL10n } from "@ews/react-localization-context";
import { IonButton, IonHeader, IonItem, IonList, IonModal, IonText, IonTitle, IonToolbar } from "@ionic/react";
import { useEffect, useRef, useState } from "react";
import { normalizeImageFile } from "../../../../utils";
import { useAlert } from "../../../Dialog";
import { useCreateSystemPlan, useSystemPlansWithoutImages } from "../../../ReactData/system";
import { useIsMobile } from "../../../Responsive";
import SettingsCheckbox from "../../../Settings/SettingsComponents/SettingsCeckbox/SettingsCeckbox";
import SettingsDropFile from "../../../Settings/SettingsComponents/SettingsDropFile/SettingsDropFile";
import SettingsNumberInput from "../../../Settings/SettingsComponents/SettingsNumberInput/SettingsNumberInput";
import SettingsTextInput from "../../../Settings/SettingsComponents/SettingsTextInput/SettingsTextInput";
import { hasMainPlan } from "../../planUtils";
import modalStyle from "./NewPlanPage.module.scss";
import { Errors, NewPlanPageProps } from "./types";
// import { floatingPromiseHandler } from "../../../../utils";


const NewPlanPageModal: React.FC<NewPlanPageProps> = ({ isOpen, systemId, list, onDismiss }) =>
{
    const plans = useSystemPlansWithoutImages(systemId || "");
    const mobileView = useIsMobile();

    function nextNumber(numbers: number[]): number
    {
        return Math.max(...numbers) + 1;
    }

    const { translate: t } = useL10n();
    const modal = useRef<HTMLIonModalElement>(null);
    const [name, setName] = useState<string | undefined>(undefined);
    const [_isMainPlan, setIsMainPlan] = useState<boolean>(false);
    const [floor, setFloor] = useState<number>(plans.length ? nextNumber(plans.map(plan => plan.floor || 0)) : 0);
    const [image, setImage] = useState<string | undefined>(undefined);
    const [error, setError] = useState<Errors>({});
    const pageIdExistState = useRef<null | boolean>(null);
    const myAlert = useAlert();

    const createPlan = useCreateSystemPlan();

    const disableBtn = Boolean(!(!Object.keys(error).length && name && pageIdExistState));

    async function confirm(label: SystemPlan["label"], floor: SystemPlan["floor"], image: SystemPlan["image"])
    {
        try {
            console.info("NewPlanPage.tsx: confirm create", label, systemId);
            if (!label || !image || floor === undefined) return;
            console.info("NewPlanPage.tsx: confirm create: label and image are here :)", label, image.length);

            const newPlan = {
                label,
                floor,
                image,
                isMain: _isMainPlan
            } satisfies Partial<SystemPlan>;

            const createdPlan = await createPlan(systemId, newPlan);
            console.info("NewPlanPage.tsx: confirm create: created", createdPlan);

            await modal.current?.dismiss();
        }
        catch (e) {
            console.error("NewPlanPage.tsx: confirm create: exception", e);

            await myAlert({
                "title": t('Fehler beim Anlegen des Plans'),
                message: String(e),
                "buttonTitle": t('Dismiss')
            });
        }
    }

    useEffect(() =>
    {
        if (floor === 0 && plans.length > 0) {
            setFloor(nextNumber(plans.map(plan => plan.floor || 1)));
        }
    });

    function onWillDismiss()
    {
        setName(undefined);
        setFloor(0);
        setError({});
        onDismiss(false);
    }
    function pageIdExist(floor: number)
    {
        const foundedPages = plans.filter(plan => plan.floor === floor);
        return Boolean(foundedPages.length);
    }

    function nameExist(name: string)
    {
        const foundedNames = plans.filter(plan => plan.label === name);
        return Boolean(foundedNames.length);
    }

    if (!isOpen) {
        return null;
    }

    return <IonModal
        ref={modal}
        className={modalStyle.newSystemModal}
        isOpen={true}
        breakpoints={mobileView ? [.6] : undefined}
        onWillDismiss={onWillDismiss}>
        <IonHeader>
            <IonToolbar>
                <div className={modalStyle.header}>
                    <IonTitle>{`${t("COM_FACPMANAGEMENT_MAPS_NEW")}`}</IonTitle>
                </div>
            </IonToolbar>
        </IonHeader>
        <IonList
            className="ion-padding"
            color="primary">
            <SettingsTextInput
                text={"COM_ADMIN_USER_HEADING_NAME"}
                value={name}
                onChange={(value) =>
                {
                    if (!value.length) {
                        setName(value);
                        setError({ name: t("[REACT_PLEASE_ENTER_A_NAME]") });
                    }
                    else if (nameExist(value)) {
                        setError({ name: t("[REACT_PLEASE_CHOOSE_ANOTHER_NAME]") });
                    } else {
                        delete error.name;
                        setName(value);
                    }
                }} />
            <SettingsNumberInput
                text={"COM_FACPMANAGEMENT_MAPS_LEVEL"}
                value={floor}
                onChange={(value) =>
                {
                    const page = Number(value);

                    if (pageIdExist(page)) {
                        setError({ page: t("[REACT_ALREADY_EXIST]") });
                    } else {
                        delete error.page;
                        setFloor(page);
                    }
                }} />
            <SettingsCheckbox
                disabled={hasMainPlan(plans)}
                text={"[REACT_MAIN_PLAN]"}
                checked={_isMainPlan}
                onChange={(checked) =>
                {
                    setIsMainPlan(checked);
                }} />
            <SettingsDropFile
                text={"[REACT_PLAN_IMAGE]"}
                accept="image/*"
                // maxSize={80_000_000} // cant use size limit of control because there it not normalized yet!
                info="JGLOBAL_MAXIMUM_UPLOAD_SIZE_LIMIT"
                //onError={(error) => console.log(error)}
                onFile={async function (file: File): Promise<void>
                {
                    try {

                        console.log("Got file", file);
                        const normalizedBase64 = await normalizeImageFile(file, {
                            targetFormat: "image/jpeg",
                            quality: 70,
                            debugLogImageInfo: true,
                        });

                        if (normalizedBase64 && normalizedBase64.length > 8_000_000) {
                            await myAlert({
                                "title": t('Fehler beim verarbeiten des Bildes'),
                                message: "Das Bild ist groesser als 8MB. Bitte waehlen sie ein anderes Bild aus.",
                                "buttonTitle": t('Dismiss')
                            });

                            return;
                        }

                        setImage(normalizedBase64);
                    }
                    catch (err) {
                        setError({ image: t("[REACT_THIS_FILE_CAN_NOT_BE_UPLOADED]") });
                    }
                }}
            />
            <IonItem lines={error ? "full" : "none"}>
                <div className={modalStyle.error}>
                    <IonText color={"danger"}>{Object.values(error).map(value => value).join(" ,")}</IonText>
                </div>
            </IonItem>
        </IonList>
        <div className={`${modalStyle.buttonContainer} ${modalStyle.marginBottom}`}>
            <IonButton
                slot="start"
                color={"danger"}
                onClick={() => modal.current?.dismiss()}>{t("COM_BANNERS_CANCEL")}</IonButton>
            <IonButton
                disabled={disableBtn}
                slot="end"
                color={"success"}
                onClick={() => confirm(name, floor, image)}>
                {t("COM_CONTENT_FORM_VIEW_DEFAULT_OPTION")}
            </IonButton>
        </div>
    </IonModal>;

};


export default NewPlanPageModal;
